import React, { useState } from 'react';
import { CreateTrendlineStrategy } from '../Strategy';

// SVG Icons
const TrendLineIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
  </svg>
);

const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const UpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const DownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

const FlatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-base-content/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
  </svg>
);

/**
 * TrendLineManager - Displays captured trend lines and allows creating strategies
 * 
 * @param {Object} props
 * @param {Array} props.trendLines - Array of trend line objects from TradingView
 * @param {Function} props.onDeleteTrendLine - Handler to delete a trend line
 * @param {string} props.symbol - Current chart symbol
 */
const TrendLineManager = ({
  trendLines = [],
  onDeleteTrendLine,
  symbol = '',
}) => {
  const [expanded, setExpanded] = useState(true);
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [selectedLines, setSelectedLines] = useState([]);

  // Calculate trend direction
  const getTrendDirection = (line) => {
    if (!line.y1 || !line.y2) return 'flat';
    const slope = (line.y2 - line.y1) / (line.x2 - line.x1);
    if (Math.abs(slope) < 0.0001) return 'flat';
    return slope > 0 ? 'up' : 'down';
  };

  // Get direction icon
  const getDirectionIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <UpIcon color="success" fontSize="small" />;
      case 'down':
        return <DownIcon color="error" fontSize="small" />;
      default:
        return <FlatIcon color="action" fontSize="small" />;
    }
  };

  // Format timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString();
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return price.toFixed(2);
  };

  // Toggle line selection
  const toggleLineSelection = (lineId) => {
    setSelectedLines(prev => 
      prev.includes(lineId)
        ? prev.filter(id => id !== lineId)
        : [...prev, lineId]
    );
  };

  // Open strategy dialog with selected lines
  const handleCreateStrategy = () => {
    setStrategyDialogOpen(true);
  };

  // Handle strategy creation success
  const handleStrategySuccess = (strategy) => {
    console.log('Strategy created:', strategy);
    setStrategyDialogOpen(false);
    setSelectedLines([]);
  };

  // Get selected trend line objects
  const getSelectedTrendLines = () => {
    return trendLines.filter(line => selectedLines.includes(line.id));
  };

  return (
    <div className="card card-border bg-base-100 p-4">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <TrendLineIcon />
          <span className="font-medium">Trend Lines</span>
          <span className={`badge badge-sm ${trendLines.length > 0 ? 'badge-primary' : 'badge-ghost'}`}>
            {trendLines.length}
          </span>
        </div>
        <button className="btn btn-ghost btn-xs btn-circle">
          {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </button>
      </div>

      {expanded && (
        <>
          <div className="divider my-2"></div>

          {trendLines.length === 0 ? (
            <div className="alert alert-info mt-2">
              <span className="text-sm">Draw trend lines on the chart to use them in your trading strategy.</span>
            </div>
          ) : (
            <>
              {/* Trend Lines List */}
              <ul className="space-y-1 max-h-72 overflow-auto">
                {trendLines.map((line, index) => {
                  const direction = getTrendDirection(line);
                  const isSelected = selectedLines.includes(line.id);

                  return (
                    <li
                      key={line.id || index}
                      className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'border-2 border-primary bg-primary/5' : 'border border-base-300 hover:bg-base-200'}`}
                      onClick={() => toggleLineSelection(line.id)}
                    >
                      <span className="mr-2">
                        {getDirectionIcon(direction)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {line.name || `Trend Line ${index + 1}`}
                        </p>
                        <p className="text-xs text-base-content/60">
                          {formatDate(line.x1)} → {formatDate(line.x2)} | 
                          ${formatPrice(line.y1)} → ${formatPrice(line.y2)}
                        </p>
                      </div>
                      <button
                        className="btn btn-ghost btn-xs btn-circle text-error ml-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDeleteTrendLine) {
                            onDeleteTrendLine(line.id);
                          }
                        }}
                        title="Remove from list"
                      >
                        <DeleteIcon />
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Action Buttons */}
              <div className="mt-4">
                <button
                  className="btn btn-primary w-full gap-1"
                  onClick={handleCreateStrategy}
                  disabled={trendLines.length === 0}
                >
                  <AddIcon />
                  Create Strategy
                  {selectedLines.length > 0 && ` (${selectedLines.length} selected)`}
                </button>
              </div>

              {selectedLines.length > 0 && (
                <p className="text-xs text-base-content/60 mt-2">
                  Selected lines will be available in the strategy builder
                </p>
              )}
            </>
          )}
        </>
      )}

      {/* Strategy Creation Dialog */}
      <CreateTrendlineStrategy
        open={strategyDialogOpen}
        onClose={() => setStrategyDialogOpen(false)}
        onSuccess={handleStrategySuccess}
        availableTrendLines={selectedLines.length > 0 ? getSelectedTrendLines() : trendLines}
        initialData={{
          tickerName: symbol.replace(/^[^:]+:/, ''), // Remove exchange prefix
        }}
      />
    </div>
  );
};

export default TrendLineManager;
